<script setup lang="ts">
import BackComponent from '@/components/BackComponent.vue';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import InputCardComponent from '@/components/InputCardComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import SliderInputComponent from '@/components/SliderInputComponent.vue';
import VehicleCardComponent from '@/components/VehicleCardComponent.vue';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import local from '@/stores/local';
import { useVehicleStore } from '@/stores/vehicle';
import type { Engine, Model } from '@/types';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const vehicleStore = useVehicleStore()
const agreementStore = useAgreementStore()
const { details, isManualInput, manualVehicle } = storeToRefs(useVehicleStore())
const { config } = storeToRefs(useConfigStore())
const { agreement } = storeToRefs(useAgreementStore())

const loading = ref(false)
const loadingModels = ref(false)
const loadingEngines = ref(false)
const disabled = ref(false)
const models = ref<Model[]>([])
const engines = ref<Engine[]>([])

// Raw text as typed by the user — may be a registration number OR a VIN.
// The store's licensePlate/vinNumber fields are only ever set from the API response below.
const carRegOrVin = ref<string>(agreementStore.agreement.licensePlate ?? '')

watch(carRegOrVin, async (value) => {
  if (!value || typeof value !== 'string') return

  const match = new RegExp(/^([\w]{7}|[\w]{17})$/).test(value)

  if (match) {
    disabled.value = true
    loading.value = true

    try {
      const vehicle = await vehicleStore.search(value)

      if (!vehicle) throw 'NOT_FOUND'

      details.value = vehicle

      agreementStore.agreement.oldMileage = Math.min((new Date().getFullYear() - vehicle.registrationYear) * 20000, config.value?.maxStartMileage as number)

      agreement.value.brandId = vehicle.brandId
      agreement.value.modelId = vehicle.modelId
      agreement.value.engineId = vehicle.engineId
      agreement.value.registrationYear = vehicle.registrationYear
      agreement.value.licensePlate = vehicle.registrationNumber
      agreement.value.vinNumber = vehicle.serviceVinNumber

      isManualInput.value = false
      manualVehicle.value = {}

      if (!vehicle.modelMatch) {
        getModels(vehicle.brandId);
      }

      if (!vehicle.engineMatch) {
        getEngines(vehicle.brandId)
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }
})

async function getModels(brandId: number) {
  loadingModels.value = true

  try {
    const { data } = await axios.get(`proxy/brands/${brandId}/models`)

    models.value = data
  } catch (error) {
    console.error(error)
  } finally {
    loadingModels.value = false
  }
}

async function getEngines(brandId: number) {
  loadingEngines.value = true

  try {
    const { data } = await axios.get(`proxy/brands/${brandId}/engines`)

    engines.value = data
  } catch (error) {
    console.error(error)
  } finally {
    loadingEngines.value = false
  }
}

async function onReset() {
  const recaptcha = await local.getItem<string>('recaptcha')

  vehicleStore.reset()
  agreementStore.reset()
  carRegOrVin.value = ''
  local.clear()
  disabled.value = false

  if (recaptcha) {
    // User was already authenticated, so reinstate that
    local.setItem<string>('recaptcha', recaptcha)
  }
}

onMounted(() => {
  if (vehicleStore.details && !vehicleStore.details.modelMatch) {
    getModels(vehicleStore.details.brandId)
  }

  if (vehicleStore.details && !vehicleStore.details.engineMatch) {
    getEngines(vehicleStore.details.brandId)
  }

  if (agreement.value.licensePlate) {
    disabled.value = true
  }
})
</script>

<template>
  <v-container>
    <v-row>
      <v-col>
        <div class="d-flex align-center mb-2">
          <div>Indtast reg.nr. eller VIN</div>
          <v-btn
            icon="mdi-car-back"
            variant="text"
            size="small"
            title="Vælg anden bil"
            @click="onReset"
            class="ml-auto"
          />
        </div>
        <v-text-field
          id="registration-input"
          ref="registrationNumberInput"
          v-model="carRegOrVin"
          variant="solo"
          :maxlength="17"
          :loading="loading"
          :disabled="disabled"
          hide-details="auto"
          autocomplete="off"
          autofocus
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-btn to="/agreement/manual-vehicle" variant="plain" :block="$vuetify.display.mobile">
          Jeg vil indtaste min bil manuelt
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VehicleCardComponent />
      </v-col>
    </v-row>
    <v-slide-x-transition>
      <v-row v-if="vehicleStore.details && !vehicleStore.details.modelMatch">
        <v-col>
          <InputCardComponent label="Hvilken model passer bedst til dit køretøj?">
            <v-autocomplete
              :model-value="!agreement.modelId || agreement.modelId === 0 ? null : agreement.modelId"
              :items="models"
              label="Vælg model"
              item-value="id"
              item-title="name"
              auto-select-first
              hide-details
              @update:model-value="(value) => { agreement.modelId = value; agreementStore.calculate(); }"
            />
          </InputCardComponent>
        </v-col>
      </v-row>
    </v-slide-x-transition>
    <v-slide-x-transition>
      <v-row v-if="vehicleStore.details && !vehicleStore.details.engineMatch">
        <v-col>
          <InputCardComponent label="Hvilken motor passer bedst til dit køretøj?">
            <v-select
              :model-value="!agreement.engineId || agreement.engineId === 0 ? null : agreement.engineId"
              :items="engines"
              label="Vælg motor"
              item-value="id"
              item-title="name"
              auto-select-first
              hide-details
              @update:model-value="(value) => { agreement.engineId = value; agreementStore.calculate(); }"
            />
          </InputCardComponent>
        </v-col>
      </v-row>
    </v-slide-x-transition>
    <v-slide-x-transition>
      <v-row v-if="vehicleStore.details">
        <v-col>
          <SliderInputComponent
            v-model="agreement.oldMileage"
            label="Hvor langt har dit køretøj kørt?"
            :min="0"
            :max="config?.maxStartMileage"
            :step="5000"
            unit="km"
          />
        </v-col>
      </v-row>
    </v-slide-x-transition>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/" />
    <NextComponent
      to="/agreement/type"
      :disabled="!vehicleStore.details || !agreement.brandId || !agreementStore.isOkay ||
       !agreement.modelId || !agreement.engineId || (vehicleStore.age !== 0 && !agreement.oldMileage)"
    />
  </BottomBarComponent>
</template>

<style>
#registration-input {
  text-transform: uppercase;
  font-size: 2rem;
  text-align: center;
  letter-spacing: 4px;
}
</style>
